import { X } from "lucide-react"
import Link from "next/link"
import {
  ComponentPropsWithoutRef,
  FunctionComponent,
  MouseEventHandler,
} from "react"

import { classNames } from "../../../../lib/classNames"
import { useCopyToClipboard } from "../../../../lib/copy"
import { WebUrl } from "../../../../lib/webUrl"
import { Icon, IconSizes, IconProps } from "../../../Icon"

type ChipSize = "normal" | "large"

export type ChipColor = "green" | "red" | "blue" | "yellow" | "gray" | "sky"

export type ChipIconSize = "small" | "default"

export interface ChipProps extends ComponentPropsWithoutRef<"span"> {
  IconInput?: IconProps["icon"]
  /** Icon size class. "small" = size-4 (16px), "default" = size-6 (24px). */
  iconSize?: ChipIconSize
  text?: string
  href?: WebUrl | string
  color: ChipColor
  size?: ChipSize
  isCopyable?: boolean
  deletable?: boolean
  deleteDataTestId?: string
  onDelete?: () => void
}

function getClassNamesForColor(
  color: ChipColor,
  linkable: boolean,
  copyable: boolean
) {
  switch (color) {
    case "green": {
      const defaultClasses =
        "bg-green-50 text-green-700 dark:bg-green-900 dark:text-green-100"
      const hoverClasses = "hover:text-green-600 dark:hover:text-green-50"
      const activeClasses = "active:text-green-800 dark:active:text-green-200"
      return classNames(
        defaultClasses,
        (linkable || copyable) && hoverClasses,
        (linkable || copyable) && activeClasses
      )
    }
    case "red": {
      const defaultClasses =
        "bg-red-50 text-red-700 dark:bg-red-900 dark:text-red-100"
      const hoverClasses = "hover:text-red-600 dark:hover:text-red-50"
      const activeClasses = "active:text-red-800 dark:active:text-red-200"
      return classNames(
        defaultClasses,
        (linkable || copyable) && hoverClasses,
        (linkable || copyable) && activeClasses
      )
    }
    case "blue": {
      const defaultClasses =
        "bg-cobalt-50 text-cobalt-400 dark:bg-cobalt-500 dark:text-cobalt-100"
      const hoverClasses = "hover:text-cobalt-300 dark:hover:text-cobalt-50"
      const activeClasses = "active:text-cobalt-500 dark:active:text-cobalt-200"
      return classNames(
        defaultClasses,
        (linkable || copyable) && hoverClasses,
        (linkable || copyable) && activeClasses
      )
    }
    case "yellow": {
      const defaultClasses =
        "bg-amber-50 text-amber-700 dark:bg-amber-900 dark:text-amber-100"
      const hoverClasses = "hover:text-amber-600 dark:hover:text-amber-50"
      const activeClasses = "active:text-amber-800 dark:active:text-amber-200"
      return classNames(
        defaultClasses,
        (linkable || copyable) && hoverClasses,
        (linkable || copyable) && activeClasses
      )
    }
    case "gray": {
      const defaultClasses =
        "bg-gray-100 text-gray-600 dark:bg-gray-600 dark:text-gray-100"
      const hoverClasses = "hover:text-gray-500 dark:hover:text-gray-50"
      const activeClasses = "active:text-gray-700 dark:active:text-gray-200"
      return classNames(
        defaultClasses,
        (linkable || copyable) && hoverClasses,
        (linkable || copyable) && activeClasses
      )
    }
    case "sky": {
      const defaultClasses =
        "bg-sky-50 text-sky-700 dark:bg-sky-900 dark:text-sky-100"
      const hoverClasses = "hover:text-sky-600 dark:hover:text-sky-50"
      const activeClasses = "active:text-sky-800 dark:active:text-sky-200"
      return classNames(
        defaultClasses,
        (linkable || copyable) && hoverClasses,
        (linkable || copyable) && activeClasses
      )
    }
    default:
      throw new Error("unexpected chip color")
  }
}

function getClassNamesForSize(size: ChipSize) {
  switch (size) {
    case "normal":
      return "text-body"
    case "large":
      return "text-display"
    default:
      throw new Error("unexpected chip size")
  }
}

export const Chip: FunctionComponent<ChipProps> = (props) => {
  const {
    color,
    size = "normal",
    IconInput,
    iconSize = "default",
    text,
    href,
    tabIndex,
    isCopyable = false,
    deletable,
    onDelete,
    className,
    deleteDataTestId,
    ...otherProps
  } = props

  const copy = useCopyToClipboard({
    subject: text,
  })
  const isLinkable = !!href

  const handleClick: MouseEventHandler<HTMLElement> = (event) => {
    event.stopPropagation()
    if (onDelete) {
      onDelete()
    }
  }

  const handleCopy: MouseEventHandler<HTMLElement> = (e) => {
    e.stopPropagation()
    copy(text ?? "")
  }

  const defaultChip = (
    <span
      className={classNames(
        "text-body inline-flex h-fit w-fit items-center whitespace-nowrap rounded-sm py-0.5",
        getClassNamesForColor(color, isLinkable, isCopyable),
        getClassNamesForSize(size),
        isLinkable && "cursor-pointer underline",
        isCopyable && "cursor-pointer",
        text ? "px-2" : "px-1",
        className
      )}
      aria-label={typeof text === "string" ? text : "chip"}
      {...(isCopyable && !isLinkable && { onClick: handleCopy })}
      {...otherProps}
    >
      {!!text && text}
      {!!IconInput && (
        <IconInput
          className={classNames(
            iconSize === "small" ? "size-4" : "size-6",
            // Space the icon from preceding text. Deletable chips have their
            // own `pl-1` button wrapper, so this only kicks in for static
            // icons after text.
            !!text && "ml-1"
          )}
        />
      )}
      {deletable && !isLinkable && (
        <button
          type="button"
          data-testid={deleteDataTestId}
          className="pl-1"
          onClick={onDelete ? handleClick : undefined}
          aria-label="Delete chip icon"
        >
          <Icon icon={X} size={IconSizes.ExtraSmall} />
        </button>
      )}
    </span>
  )

  return (
    <>
      {isLinkable ? (
        <Link
          href={href}
          target="_blank"
          tabIndex={tabIndex}
          onClick={(e) => e.stopPropagation()}
        >
          {defaultChip}
        </Link>
      ) : (
        defaultChip
      )}
    </>
  )
}
